import { allDocuments } from "@/lib/tree";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ListTree, Sparkles, BookOpen, Download } from "lucide-react";

export default function Overview({ project, status, tree, onGoTo }) {
  const docs = allDocuments(tree);
  const counts = status?.counts || {};
  const total = counts.total || docs.length || 0;
  const approved = counts.approved || 0;
  const generated = (counts.generated || 0) + (counts.reviewing || 0) + approved;
  const pct = total ? Math.round((generated / total) * 100) : 0;

  const stats = [
    { label: "Documents", value: total },
    { label: "Generated", value: generated },
    { label: "Approved", value: approved },
    { label: "Waiting", value: counts.waiting || 0 },
  ];

  const steps = [
    { icon: ListTree, title: "Build the index", desc: "Generate or upload your structure blueprint.", to: "index" },
    { icon: Sparkles, title: "Generate documents", desc: "Launch the AI queue across your scope.", to: "generate" },
    { icon: BookOpen, title: "Review & edit", desc: "Refine with the editor and AI actions.", to: "documents" },
    { icon: Download, title: "Export & publish", desc: "PDF, Master PDF, ZIP, DOCX & more.", to: "export" },
  ];

  return (
    <div className="animate-fade-in space-y-8">
      <div>
        <h2 className="font-serif text-3xl font-medium tracking-tight">{project.title}</h2>
        {project.purpose && <p className="mt-2 max-w-2xl text-muted-foreground">{project.purpose}</p>}
        <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
          {project.tone && <span className="rounded-sm border border-border px-2 py-1">Tone: {project.tone}</span>}
          {project.audience && (
            <span className="rounded-sm border border-border px-2 py-1">Audience: {project.audience}</span>
          )}
          <span className="rounded-sm border border-border px-2 py-1">
            Mode: {project.default_mode?.replace("_", " ")}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-sm border border-border bg-card p-5">
            <div className="font-serif text-3xl font-semibold">{s.value}</div>
            <div className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      {total > 0 && (
        <div className="rounded-sm border border-border bg-card p-5">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Publication progress</span>
            <span className="font-medium">{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" data-testid="overview-progress" />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {steps.map((s, i) => (
          <button
            key={s.title}
            onClick={() => onGoTo(s.to)}
            data-testid={`overview-step-${s.to}`}
            className="group flex items-start gap-4 rounded-sm border border-border bg-card p-6 text-left transition-colors hover:border-primary/50"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-secondary text-primary">
              <s.icon size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Step {i + 1}</span>
              </div>
              <div className="font-serif text-lg font-semibold">{s.title}</div>
              <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
